import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable, fromEvent, Subject } from 'rxjs';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { UserAttributesI, UserSocketLoginI } from '@interfaces/user.interface';
import { UserHelperService } from '@helpers/user-helper.service';
import { TaskI } from '@interfaces/task/task.interface';
import { SessionStorageService } from '@services/common/session-storage.service';

@Injectable({
  providedIn: 'root',
})
export class TaskSocketService {
  private static readonly HEADER_USER_ID = 'x-user-id';
  private static readonly HEADER_SESSION_ID = 'x-session-id';
  private static readonly HEADER_CUSTOMER_ID = 'x-customer-id';
  private static readonly HEADER_ACCESS_LEVEL = 'x-access-level';

  private readonly socketUrl = environment.OMNISMART_SOCKET_URL;
  private readonly namespace = 'task';
  private socket?: Socket;
  private readonly notificationSubject = new Subject<any | undefined>();
  public notificationFeed$ = this.notificationSubject.asObservable();

  private static readonly EVENT_TASK_NOTIFICATION = 'task-notification';
  private readonly socketOptions: Partial<ManagerOptions & SocketOptions> = {
    reconnection: true,
    timeout: 5000,
  };
  constructor(private userHelperService: UserHelperService, private sessionStorageService: SessionStorageService) {}

  public init(): void {
    const user = this.getUserData(this.userHelperService.getUserAttributes());
    if (user && !this.socket) {
      this.initializeSocket(user);
      this.registerEvents();
    }
  }

  private initializeSocket(user: UserSocketLoginI): void {
    const namespacePrefix = this.socketUrl.endsWith('/') ? '' : '/';
    this.socket = io(this.socketUrl + namespacePrefix + this.namespace, {
      ...this.socketOptions,
      extraHeaders: {
        [TaskSocketService.HEADER_USER_ID]: user._id,
        [TaskSocketService.HEADER_CUSTOMER_ID]: user.customerId,
        [TaskSocketService.HEADER_ACCESS_LEVEL]: user.level?.toString() || '0',
        [TaskSocketService.HEADER_SESSION_ID]: this.sessionStorageService.getSessionId()!,
      },
    });
  }

  private registerEvents(): void {
    this.socket?.on(TaskSocketService.EVENT_TASK_NOTIFICATION, (data: any) => {
      this.notificationSubject.next(data);
    });
  }

  private getUserData(user: UserAttributesI | void): UserSocketLoginI | void {
    if (!user) return;

    return {
      _id: user['custom:userId'],
      customerId: user['custom:customer'],
      teams: user['custom:team'],
      team: user['custom:team'],
    } as UserSocketLoginI;
  }
}
